import type { Editor, Range } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import { ReactRenderer } from "@tiptap/react";
import type { SuggestionOptions, SuggestionProps } from "@tiptap/suggestion";
import tippy, { type Instance as TippyInstance } from "tippy.js";
import type { ChipOption } from "components/editor/email-editor";
import {
  VariableChipList,
  type VariableChipListRef,
} from "components/editor/email-editor/extensions/variable-chip/VariableChipList";

const MAX_SUGGESTION_RESULTS = 20;

// ─── Match finder ────────────────────────────────────────────────────

/**
 * Custom suggestion match finder for the `{{` trigger.
 *
 * Detects `{{` followed by optional word/dash characters and returns
 * the match range + query text. For example, typing `{{cust` will
 * return query = "cust".
 */
const findChatVariableMatch = (config: {
  char: string;
  $position: {
    pos: number;
    nodeBefore: { isText: boolean; text: string } | null;
  };
}): { range: { from: number; to: number }; query: string; text: string } | null => {
  const { char, $position } = config;
  const nodeBefore = $position.nodeBefore;

  if (!nodeBefore?.isText || !nodeBefore.text) return null;

  const text = nodeBefore.text;
  const textFrom = $position.pos - text.length;

  const regexp = new RegExp(`${escapeRegex(char)}([\\w-]*)$`);
  const match = text.match(regexp);

  if (!match || match.index === undefined) return null;

  const from = textFrom + match.index;
  const to = $position.pos;

  return {
    range: { from, to },
    query: match[1] || "",
    text: match[0],
  };
};

const escapeRegex = (str: string): string =>
  str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// ─── Factory ─────────────────────────────────────────────────────────

/**
 * Creates the suggestion configuration for the ChatVariable extension.
 *
 * On selection, the chosen variable is resolved to its literal value
 * (via `resolveRef`) and inserted as plain text — unlike the email
 * editor which inserts a chip node.
 *
 * @param chipOptionsRef - Ref to the current chip options list. Using a
 *   ref lets us change/disable options without recreating the editor.
 * @param resolveRef - Ref to a resolver function that returns the literal
 *   value for a given chip id. Using a ref ensures the latest ticket
 *   data is always read.
 */
export const createChatVariableSuggestion = (
  chipOptionsRef: React.RefObject<ChipOption[]>,
  resolveRef: React.RefObject<(id: string) => string>,
): Partial<SuggestionOptions<ChipOption>> => ({
  char: "{{",
  pluginKey: new PluginKey("chatVariableSuggestion"),

  findSuggestionMatch:
    findChatVariableMatch as SuggestionOptions["findSuggestionMatch"],

  command: ({
    editor,
    range,
    props,
  }: {
    editor: Editor;
    range: Range;
    props: ChipOption;
  }) => {
    const resolved = resolveRef.current?.(props.id) ?? "";
    editor.chain().focus().deleteRange(range).insertContent(resolved).run();
  },

  items: ({ query }: { query: string }): ChipOption[] => {
    const options = chipOptionsRef.current ?? [];
    if (options.length === 0) return [];

    const lowerQuery = query.toLowerCase();
    if (!lowerQuery) return options.slice(0, MAX_SUGGESTION_RESULTS);

    return options
      .filter((opt) => opt.label.toLowerCase().includes(lowerQuery))
      .slice(0, MAX_SUGGESTION_RESULTS);
  },

  render: () => {
    let component: ReactRenderer<VariableChipListRef>;
    let popup: TippyInstance[];

    return {
      onStart: (props: SuggestionProps<ChipOption>) => {
        component = new ReactRenderer(VariableChipList, {
          props,
          editor: props.editor,
        });

        if (!props.clientRect) return;

        popup = tippy("body", {
          getReferenceClientRect: props.clientRect as () => DOMRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: "manual",
          placement: "top-start",
        });
      },

      onUpdate(props: SuggestionProps<ChipOption>) {
        component.updateProps(props);

        if (!props.clientRect) return;

        popup[0].setProps({
          getReferenceClientRect: props.clientRect as () => DOMRect,
        });
      },

      onKeyDown(props: { event: KeyboardEvent }): boolean {
        if (props.event.key === "Escape") {
          popup[0].hide();
          return true;
        }
        return component.ref?.onKeyDown(props.event) ?? false;
      },

      onExit() {
        popup[0]?.destroy();
        component?.destroy();
      },
    };
  },
});
